import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Button } from "@material-ui/core";
import DataUsage from "@material-ui/icons/DataUsage";
import EmailDashboardAPIs, {
    iCreditUsage,
    iCredUsageStats,
    iCreditUsedLimit,
    isAccountOverQuota,
} from "../../apis/Email/email.dashboard.apis";
import "./upgrade-plan-dialog.scss";

const KEY = "last_time_upgrade_check";

const USAGE_KEYS = ["campaigns", "lists", "sending_limit", "subscribers"] as const;
type UsageKey = (typeof USAGE_KEYS)[number];

const QUOTA_LABELS: Record<UsageKey, string> = {
    campaigns: "Campaigns",
    lists: "Mailing lists",
    sending_limit: "Email sending",
    subscribers: "Subscribers",
};

function isCredShape(v: unknown): v is iCredUsageStats | iCreditUsedLimit {
    return (
        typeof v === "object" &&
        v !== null &&
        "progress" in v &&
        typeof (v as { progress: unknown }).progress === "number"
    );
}

function pickUsagePayload(res: iCreditUsage): iCreditUsage | null {
    const raw = res as unknown as Record<string, unknown>;
    const base: Partial<iCreditUsage> = {};
    let ok = false;
    for (const k of USAGE_KEYS) {
        const v = raw[k];
        if (isCredShape(v)) {
            (base as Record<string, iCredUsageStats | iCreditUsedLimit>)[k] = v;
            ok = true;
        }
    }
    return ok ? (base as iCreditUsage) : null;
}

function getExceededEntries(data: iCreditUsage): Array<{
    key: UsageKey;
    label: string;
    stats: iCredUsageStats | iCreditUsedLimit;
}> {
    const payload = pickUsagePayload(data);
    if (!payload) return [];
    return USAGE_KEYS.filter((k) => {
        const u = payload[k];
        return u && u.progress > 99;
    }).map((k) => ({
        key: k,
        label: QUOTA_LABELS[k],
        stats: payload[k],
    }));
}

export default function UpgradePlanDialog() {
    const [show, setShow] = useState(false);
    const [usageSnapshot, setUsageSnapshot] = useState<iCreditUsage | null>(null);

    const handleClose = () => setShow(false);
    const handleShow = (usage: iCreditUsage) => {
        setUsageSnapshot(usage);
        setShow(true);
        localStorage.setItem(KEY, new Date().getTime().toString());
    };

    const exceeded = useMemo(
        () => (usageSnapshot ? getExceededEntries(usageSnapshot) : []),
        [usageSnapshot]
    );

    useEffect(() => {
        const last_checked = window.localStorage.getItem(KEY);
        const now = new Date().getTime();
        if (!last_checked || now - parseInt(last_checked, 10) > 24 * 1000 * 60 * 60) {
            new EmailDashboardAPIs()
                .credits_usage()
                .then((res) => {
                    if (isAccountOverQuota(res)) {
                        handleShow(res);
                    }
                })
                .catch(() => {});
        }
    }, []);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            dialogClassName="upgrade-plan-dialog"
        >
            <Modal.Header closeButton>
                <div className="upgrade-plan-dialog__header-main w-100">
                    <div className="upgrade-plan-dialog__icon-wrap" aria-hidden>
                        <DataUsage />
                    </div>
                    <div className="min-w-0">
                        <Modal.Title id="upgrade-plan-dialog-title">Plan limit reached</Modal.Title>
                        <p className="upgrade-plan-dialog__subtitle">
                            One or more resources on your current plan are at capacity. Upgrade to keep using
                            Emailwish without interruptions.
                        </p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                <p className="upgrade-plan-dialog__lead">
                    {exceeded.length === 1 ? (
                        <>
                            {`You're over your plan allowance for `}
                            <strong>{exceeded[0].label}</strong>
                            {`. Details below.`}
                        </>
                    ) : exceeded.length > 1 ? (
                        <>The following are over your plan allowance:</>
                    ) : (
                        <>Your plan usage is over the allowed quota. Upgrade to unlock full access.</>
                    )}
                </p>
                {exceeded.length > 0 ? (
                    <ul className="upgrade-plan-dialog__list" aria-label="Exceeded quotas">
                        {exceeded.map(({ key, label, stats }) => (
                            <li key={key} className="upgrade-plan-dialog__item">
                                <div className="upgrade-plan-dialog__item-head">
                                    <span className="upgrade-plan-dialog__item-label">{label}</span>
                                    <span className="upgrade-plan-dialog__item-pct">
                                        {Math.round(stats.progress)}%
                                    </span>
                                </div>
                                <div className="upgrade-plan-dialog__bar" role="presentation">
                                    <div
                                        className="upgrade-plan-dialog__bar-fill"
                                        style={{ width: `${Math.min(100, stats.progress)}%` }}
                                    />
                                </div>
                                {(stats.progress_text || stats.quota) && (
                                    <p className="upgrade-plan-dialog__item-detail">
                                        {stats.progress_text}
                                        {stats.progress_text && stats.quota ? " · " : ""}
                                        {stats.quota}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </Modal.Body>

            <Modal.Footer className="upgrade-plan-dialog__footer d-flex flex-wrap align-items-center justify-content-end">
                <Button type="button" variant="outlined" color="primary" onClick={handleClose}>
                    Not now
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className="positive-button"
                    component={Link}
                    to="/account/subscription"
                    onClick={handleClose}
                >
                    View plans &amp; upgrade
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
